
import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, Upload } from "lucide-react";
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Paper,
  Collapse,
  Grid,
} from "@mui/material";

const VariableSourceEditor = ({ variable, environmentId, onUpdate, isUpdating }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [sourceType, setSourceType] = useState("FromFile");
  const [sourceForm, setSourceForm] = useState({
    filename: "",
    sheetId: "",
    cell: "",
    scope: "Single Run",
    dbquery: "",
    file: null,
  });

  useEffect(() => {
    const source = variable.testdata_source || variable.testDataSource || "";
    if ((source || "").toLowerCase() === "fromfile") {
      const fileField = variable.fileField || "";
      const [sheetId, cell] = fileField.includes("~") ? fileField.split("~") : ["", ""];
      setSourceType("FromFile");
      setSourceForm({
        filename: variable.filename || "",
        sheetId: sheetId || "",
        cell: cell || "",
        scope: variable.scope || "Single Run",
        dbquery: "",
        file: null,
      });
    } else if ((source || "").toLowerCase() === "fromdb") {
      setSourceType("FromDB");
      setSourceForm({
        filename: "",
        sheetId: "",
        cell: "",
        scope: "Single Run",
        dbquery: variable.dbquery || "",
        file: null,
      });
    } else {
      setSourceType("FromFile");
      setSourceForm({
        filename: "",
        sheetId: "",
        cell: "",
        scope: "Single Run",
        dbquery: "",
        file: null,
      });
    }
  }, [variable]);

  const buildUpdateSourcePayload = () => {
    if (sourceType === "FromFile") {
      const fileField = `${sourceForm.sheetId || ""}~${sourceForm.cell || ""}`;
      return {
        variableId: variable.id,
        testDataSource: "FromFile",
        fileField,
        scope: sourceForm.scope,
        environmentId,
        filename: sourceForm.filename,
        sheetId: sourceForm.sheetId,
        cell: sourceForm.cell,
      };
    }
    return {
      variableId: variable.id,
      testDataSource: "FromDB",
      dbquery: sourceForm.dbquery,
      environmentId,
    };
  };

  const handleSubmit = () => {
    const formFields = buildUpdateSourcePayload();
    onUpdate({
      formFields,
      file: sourceType === "FromFile" ? sourceForm.file : undefined,
    });
  };

  const getSourceDisplay = () => {
    if (sourceType === "FromDB" && sourceForm.dbquery) {
      return (
        <Typography variant="caption" sx={{ color: "#4b5563" }}>
          <Box component="span" sx={{ fontWeight: 500 }}>Source:</Box> Database Query
        </Typography>
      );
    }
    if (sourceType === "FromFile" && sourceForm.filename) {
      return (
        <Typography variant="caption" sx={{ color: "#4b5563" }}>
          <Box component="span" sx={{ fontWeight: 500 }}>Source:</Box> {sourceForm.filename}
          {sourceForm.sheetId && ` (${sourceForm.sheetId}${sourceForm.cell ? `:${sourceForm.cell}` : ""})`}
        </Typography>
      );
    }
    return (
      <Typography variant="caption" sx={{ color: "#9ca3af", fontStyle: "italic" }}>
        No source configured
      </Typography>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        border: "1px solid #e5e7eb",
        borderRadius: 1,
        bgcolor: "#f9fafb",
      }}
    >

      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          "&:hover": { bgcolor: "#f3f4f6" },
          transition: "background-color 0.2s",
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Box sx={{ flex: 1 }}>{getSourceDisplay()}</Box>
        <Box sx={{ color: "#9ca3af", "&:hover": { color: "#4b5563" } }}>
          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </Box>
      </Box>

  
      <Collapse in={isExpanded}>
        <Box
          sx={{
            px: 3,
            py: 3,
            borderTop: "1px solid #e5e7eb",
            bgcolor: "white",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: "0.875rem", color: "#4b5563" }}>
                Test Data Source
              </InputLabel>
              <Select
                value={sourceType}
                onChange={(e) => setSourceType(e.target.value)}
                label="Test Data Source"
                sx={{
                  fontSize: "0.875rem",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e5e7eb",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e5e7eb",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#60a5fa",
                    borderWidth: "1px",
                  },
                }}
              >
                <MenuItem value="FromFile" sx={{ fontSize: "0.875rem" }}>
                  From File
                </MenuItem>
                <MenuItem value="FromDB" sx={{ fontSize: "0.875rem" }}>
                  From DB
                </MenuItem>
              </Select>
            </FormControl>


            {sourceType === "FromDB" && (
              <Box>
                <Typography variant="body2" sx={{ color: "#4b5563", mb: 2, fontSize: "0.875rem" }}>
                  DB Query
                </Typography>
                <TextField
                  multiline
                  rows={4}
                  fullWidth
                  value={sourceForm.dbquery}
                  onChange={(e) => setSourceForm((p) => ({ ...p, dbquery: e.target.value }))}
                  placeholder="Enter your database query"
                  size="small"
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: "0.875rem",
                      fontFamily: "monospace",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e5e7eb",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e5e7eb",
                    },
                    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#60a5fa",
                      borderWidth: "1px",
                    },
                  }}
                />
              </Box>
            )}

          
            {sourceType === "FromFile" && (
              <>
                <Box>
                  <Typography variant="body2" sx={{ color: "#4b5563", mb: 2, fontSize: "0.875rem" }}>
                    Upload File
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Button
                      component="label"
                      variant="contained"
                      startIcon={<Upload size={16} />}
                      sx={{
                        bgcolor: "#2563eb",
                        textTransform: "none",
                        fontSize: "0.875rem",
                        px: 2,
                        py: 1,
                        "&:hover": { bgcolor: "#1d4ed8" },
                      }}
                    >
                      Choose File
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        hidden
                        onChange={(e) => {
                          const f = e.target.files?.[0] || null;
                          setSourceForm((p) => ({ ...p, file: f, filename: f?.name || p.filename }));
                        }}
                      />
                    </Button>
                    {sourceForm.file && (
                      <Typography variant="body2" sx={{ color: "#4b5563", fontSize: "0.875rem" }}>
                        {sourceForm.file.name}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <TextField
                  fullWidth
                  label="File Name"
                  value={sourceForm.filename}
                  onChange={(e) => setSourceForm((p) => ({ ...p, filename: e.target.value }))}
                  placeholder="filename.xlsx"
                  size="small"
                  InputLabelProps={{
                    sx: { fontSize: "0.875rem", color: "#4b5563" },
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: "0.875rem",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e5e7eb",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e5e7eb",
                    },
                    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#60a5fa",
                      borderWidth: "1px",
                    },
                  }}
                />

                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Sheet"
                      value={sourceForm.sheetId}
                      onChange={(e) => setSourceForm((p) => ({ ...p, sheetId: e.target.value }))}
                      placeholder="Sheet1"
                      size="small"
                      InputLabelProps={{
                        sx: { fontSize: "0.875rem", color: "#4b5563" },
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          fontSize: "0.875rem",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#e5e7eb",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#e5e7eb",
                        },
                        "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#60a5fa",
                          borderWidth: "1px",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Cell"
                      value={sourceForm.cell}
                      onChange={(e) => setSourceForm((p) => ({ ...p, cell: e.target.value }))}
                      placeholder="A1"
                      size="small"
                      InputLabelProps={{
                        sx: { fontSize: "0.875rem", color: "#4b5563" },
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          fontSize: "0.875rem",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#e5e7eb",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#e5e7eb",
                        },
                        "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#60a5fa",
                          borderWidth: "1px",
                        },
                      }}
                    />
                  </Grid>
                </Grid>

                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: "0.875rem", color: "#4b5563" }}>
                    Scope
                  </InputLabel>
                  <Select
                    value={sourceForm.scope}
                    onChange={(e) => setSourceForm((p) => ({ ...p, scope: e.target.value }))}
                    label="Scope"
                    sx={{
                      fontSize: "0.875rem",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e5e7eb",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e5e7eb",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#60a5fa",
                        borderWidth: "1px",
                      },
                    }}
                  >
                    <MenuItem value="Single Run" sx={{ fontSize: "0.875rem" }}>
                      Single Run
                    </MenuItem>
                    <MenuItem value="Multi Run" sx={{ fontSize: "0.875rem" }}>
                      Multi Run
                    </MenuItem>
                    <MenuItem value="Multi Execution" sx={{ fontSize: "0.875rem" }}>
                      Multi Execution
                    </MenuItem>
                  </Select>
                </FormControl>
              </>
            )}

            
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 2 }}>
              <Button
                onClick={() => setIsExpanded(false)}
                variant="outlined"
                sx={{
                  px: 2,
                  py: 1,
                  fontSize: "0.875rem",
                  color: "#6b7280",
                  borderColor: "#e5e7eb",
                  textTransform: "none",
                  "&:hover": {
                    color: "#374151",
                    borderColor: "#e5e7eb",
                    bgcolor: "#f9fafb",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isUpdating}
                variant="contained"
                sx={{
                  px: 2,
                  py: 1,
                  fontSize: "0.875rem",
                  bgcolor: "#2563eb",
                  textTransform: "none",
                  "&:hover": { bgcolor: "#1d4ed8" },
                  "&.Mui-disabled": {
                    opacity: 0.5,
                    bgcolor: "#2563eb",
                    color: "white",
                  },
                }}
              >
                {isUpdating ? "Saving..." : "Save Source"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default VariableSourceEditor;